import { convertToRaw, EditorState } from 'draft-js';
import moment from 'moment';
import firebase from '../../firebase-integration';
import { IPostListConfig } from '../../store/posts/reducers';
import { IHighLight, IPost } from '../interfaces';
import { addTagsToSearchTerms, searchTerms } from '../utils';
import { values } from '../../contants/values';

const basePostData: IPost = {
  title: '',
  group: {
    id: '',
    name: '',
    type: 'Private',
  },
  date: '',
  owner: '',
  admins: [],
  creation: '',
  updated: '',
  content: JSON.stringify(convertToRaw(EditorState.createEmpty().getCurrentContent())),
  linkUrl: '',
  tags: [],
  posted: false,
  public: false,
  searchTerms: [],
  archived: false,
  liveTime: '',
};

const baseHighlight: IHighLight = {
  postId: '',
  owner: '',
  image: '',
  content: '',
  notificationDate: '',
  creation: '',
  updated: '',
  textColor: '',
  textSize: '',
  textPosition: '',
  backgroundColor: '',
};

const db = firebase.firestore();
const postCollection = db.collection('posts');

export const createPost = async (userId: string): Promise<object> => {
  if (!userId) {
    throw new Error('No User Information has been provided');
  }
  const date = firebase.firestore.FieldValue.serverTimestamp();
  const newPostContent: any = {
    ...basePostData,
    date,
    owner: userId,
    creation: date,
    updated: date,
    searchTerms: searchTerms([basePostData?.title]),
  };

  const newPost = await postCollection
    .add(newPostContent);

  await postCollection
    .doc(newPost.id)
    .collection('note')
    .doc(newPost.id)
    .set({
      note: JSON.stringify(convertToRaw(EditorState.createEmpty().getCurrentContent())),
    });

  return {
    post: {
      id: newPost.id,
      ...newPostContent,
      date: moment().format('MM/DD/YYYY'),
      creation: moment().format('MM/DD/YYYY HH:mm:ss ZZ'),
      updated: moment().format('MM/DD/YYYY HH:mm:ss ZZ'),
    },
  };
};

export const deletePost = async (postId: string): Promise<void> => {
  return await postCollection
    .doc(postId)
    .update({
      archived: true,
      updated: firebase.firestore.FieldValue.serverTimestamp(),
    });
};

export const updatePost = async (post: IPost): Promise<void> => {
  const postUpdates = post.title
    ? { ...post, searchTerms: searchTerms([post.title]) }
    : { ...post };

  const groupUpdates = post.group
    ? { ...postUpdates, public: post.group?.type === 'Public' }
    : { ...postUpdates };

  const tagUpdates = post.tags
      ? { ...groupUpdates, searchTerms: addTagsToSearchTerms(groupUpdates.searchTerms, post?.tags) }
      : { ...groupUpdates };

  const finalUpdates = tagUpdates.date
    ? { ...tagUpdates, date: firebase.firestore.Timestamp.fromDate(new Date(tagUpdates.date)) }
    : { ...tagUpdates };
  delete finalUpdates.id;
  return await postCollection
    .doc(post.id)
    .update({
      ...finalUpdates,
      updated: firebase.firestore.FieldValue.serverTimestamp(),
    });
};

export const getTags = async (userId: string): Promise<object> => {
  const allTags : string[] = [];
  await postCollection
      .where('archived', '==', false)
      .where('owner', '==', userId)
      .get()
      .then((postSnapshot) => {
        postSnapshot.forEach((postDocument) => {
          const data = postDocument.data();
          if (data && data.tags && data.tags.length) {
            data.tags.forEach((tag: string)  => {
              if (!allTags.includes(tag)) {
                allTags.push(tag);
              }
            });
          }
        });
      });
  return {
    tags: allTags,
  };
};

export const getPosts = async (userId: string, config: IPostListConfig): Promise<object> => {
  const documentQuery = postCollection
    .where('archived', '==', false)
    .where('owner', '==', userId);

  // Utility Builders
  const queryBuilder = (validator: boolean, queryStatement: any[]) => (baseQuery: any) =>
    validator
      ? baseQuery.where(...queryStatement)
      : baseQuery;

  const orderQueryBuilder = (validator: boolean, key: string) => (baseQuery: any) => {
    return validator ? baseQuery.orderBy(key, 'desc') : baseQuery.orderBy(key);
  };

  const paginationBuilder = (lastItem: object | null) => (baseQuery: any) =>
    config.sort.includes('-descend') && !config.pagination.lastItem
      ? baseQuery.endBefore(lastItem)
      : baseQuery.startAfter(lastItem);

  const onlyQueryPublic = queryBuilder(
    config.filter === 'public',
    ['group.type', '==', 'Public'],
  );

  const onlyQueryClosed = queryBuilder(
    config.filter === 'closed',
    ['group.type', '==', 'Closed'],
  );

  const onlyQueryPrivate = queryBuilder(
    config.filter === 'private',
    ['group.type', '==', 'Private'],
  );

  const onlyQueryNotPosted = queryBuilder(
    config.filter === 'not-posted',
    ['posted', '==', false],
  );

  const onlyQueryPosted = queryBuilder(
    config.filter === 'posted',
    ['posted', '==', true],
  );

  const searchTermQuery = (baseQuery: any) => {
    return config.search
      ? baseQuery.where('searchTerms', 'array-contains', config.search)
      : baseQuery;
  };

  const orderedQuery = (key: string) => orderQueryBuilder(
    config.sort.includes(`${key}-descend`),
    key,
  );

  const sortOrderSetup = (baseQuery: any) =>
    config.sort.includes('title') || config.sort.includes('title-descend')
      ? orderedQuery('title')(baseQuery)
      : orderedQuery('date')(baseQuery);

  const paginate = paginationBuilder(config.pagination.lastItem);
  const buildQuery = (query: any) => [query]
    .map(onlyQueryPosted)
    .map(onlyQueryNotPosted)
    .map(onlyQueryPublic)
    .map(onlyQueryClosed)
    .map(onlyQueryPrivate)
    .map(searchTermQuery)
    .map(sortOrderSetup)
    .map(paginate)
    .reduce((acc, value) => {
      return value;
    }, {});

  const results = await buildQuery(documentQuery)
    .limit(config.pagination.limit)
    .get();

  const posts: IPost[] = [];
  await results?.forEach(async (data: any) => {
    if (data.data().date) {
      if ((!(config.tags && config.tags !== 'all') ||
      data.data().tags?.length && data.data().tags?.includes(config.tags)) &&
        (!(config.groups && config.groups !== 'all') ||
        data.data().group?.id === config.groups)
      ) {
        posts.push({
          ...data.data(),
          date: moment(data.data().date.seconds * 1000).format('MM/DD/YYYY'),
          updated: moment(data.data().updated.seconds * 1000).format('MM/DD/YYYY HH:mm:ss ZZ'),
          creation: moment(data.data().creation.seconds * 1000).format('MM/DD/YYYY HH:mm:ss ZZ'),
          id: data.id,
          highlights: [],
        });
      }
    }
  });
  return {
    posts,
    lastItem: results.docs[results.docs.length - 1],
  };
};

export const getPostsByGroupId =
  async (leaderId: string, groupId: string, config: IPostListConfig): Promise<object> => {
    const documentQuery = postCollection
      .where('archived', '==', false)
      .where('owner', '==', leaderId)
      .where('group.id', '==', groupId)
      .where('posted', '==', true);

    // Utility Builders
    const queryBuilder = (validator: boolean, queryStatement: any[]) => (baseQuery: any) =>
      validator
        ? baseQuery.where(...queryStatement)
        : baseQuery;

    const orderQueryBuilder = (validator: boolean, key: string) => (baseQuery: any) => {
      return validator ? baseQuery.orderBy(key, 'desc') : baseQuery.orderBy(key);
    };

    const paginationBuilder = (lastItem: object | null) => (baseQuery: any) =>
      config.sort.includes('-descend') && !config.pagination.lastItem
        ? baseQuery.endBefore(lastItem)
        : baseQuery.startAfter(lastItem);

    const onlyQueryPublic = queryBuilder(
      config.filter === 'public',
      ['public', '==', true],
    );

    const searchTermQuery = (baseQuery: any) => {
      return config.search
        ? baseQuery.where('searchTerms', 'array-contains', config.search)
        : baseQuery;
    };

    const orderedQuery = (key: string) => orderQueryBuilder(
      config.sort.includes(`${key}-descend`),
      key,
    );

    const paginate = paginationBuilder(config.pagination.lastItem);

    const buildQuery = (query: any) => [query]
      .map(onlyQueryPublic)
      .map(searchTermQuery)
      .map(orderedQuery('date'))
      .map(orderedQuery('title'))
      .map(paginate)
      .reduce((acc, value) => {
        return value;
      }, {});

    const results = await buildQuery(documentQuery)
      // .limit(config.pagination.limit)
      .get();

    const posts: IPost[] = [];
    results.forEach((data: any) => {
      console.log('data:', data.data());
      if (!(config.tags && config.tags !== 'all') ||
          data.data().tags?.length && data.data().tags.includes(config.tags)) {
        posts.push({
          ...data.data(),
          date: moment(data.data().date.seconds * 1000).format('MM/DD/YYYY'),
          id: data.id,
          highlights: [],
        });
      }
    });

    return {
      posts,
      lastItem: results.docs[results.docs.length - 1],
    };
  };

export const getPostDetails = async (postId: string): Promise<IPost> => {
  const post = await postCollection
    .doc(postId)
    .get();

  const note: any = await getPostNote(postId);
  const highlights: any[] = await getPostHighlights(postId);

  return {
    ...post.data(),
    highlights,
    id: post.id,
    date:
      moment(post.data()?.date?.seconds ?
          post.data()?.date?.seconds * 1000 : post.data()?.date || null).format('MM/DD/YYYY'),
    updated:
      moment(post.data()?.updated?.seconds * 1000 || null).format('MM/DD/YYYY HH:mm:ss ZZ'),
    creation:
      moment(post.data()?.creation?.seconds ?
          post.data()?.creation?.seconds * 1000 : post.data()?.creation || null).format('MM/DD/YYYY HH:mm:ss ZZ'),
    note: note.note,
  };
};

export const getPostHighlights = async (postId: string): Promise<IHighLight[]> => {
  const highlightsRes = await postCollection
    .doc(postId)
    .collection('highlights')
    .orderBy('order')
    .get();

  const highlights: any[] = [];
  highlightsRes.forEach((doc) => {
    highlights.push({
      id: doc.id,
      ...doc.data(),
      notificationDate: moment(doc.data()?.notificationDate.seconds * 1000).format('MM/DD/YYYY'),
      creation: moment(doc.data()?.creation.seconds * 1000).format('MM/DD/YYYY'),
      updated: moment(doc.data()?.updated.seconds * 1000).format('MM/DD/YYYY'),
    });
  });

  return highlights;
};

export const getPostNote = async (postId: string): Promise<object> => {
  const note = await postCollection
    .doc(postId)
    .collection('note')
    .doc(postId)
    .get();

  return {
    ...note.data(),
  };
};

export const createHighlight =
  async (postId: string, userId: string, order: number): Promise<object> => {
    const highlight = {
      ...baseHighlight,
      postId,
      order,
      owner: userId,
      notificationDate: firebase.firestore.FieldValue.serverTimestamp(),
      creation: firebase.firestore.FieldValue.serverTimestamp(),
      updated: firebase.firestore.FieldValue.serverTimestamp(),
    };

    delete highlight.id;

    const result: any = await postCollection
      .doc(postId)
      .collection('highlights')
      .add(highlight);

    return {
      id: result.id,
      ...highlight,
      notificationDate: moment().format('MM/DD/YYYY'),
      creation: moment().format('MM/DD/YYYY'),
      updated: moment().format('MM/DD/YYYY'),
    };
  };

export const updateHighlight = async (highlight: IHighLight): Promise<void> => {
  const id = highlight.id;
  const postId = highlight.postId;
  const modHighlight: any = { ...highlight };
  delete modHighlight.id;
  delete modHighlight.postId;

  if (highlight.notificationDate) {
    modHighlight.notificationDate =
      firebase.firestore.Timestamp.fromDate(new Date(highlight.notificationDate));
  }

  if (highlight.creation) {
    modHighlight.creation =
      firebase.firestore.Timestamp.fromDate(new Date(highlight.creation));
  }

  return await postCollection
    .doc(postId)
    .collection('highlights')
    .doc(id)
    .update({
      ...modHighlight,
      updated: firebase.firestore.FieldValue.serverTimestamp(),
    });
};

export const deleteHighlight = async (highlight: IHighLight): Promise<void> => {
  return await postCollection
    .doc(highlight.postId)
    .collection('highlights')
    .doc(highlight.id)
    .delete();
};

export const getHighlights = async (postId: string): Promise<object[]> => {
  const results: any = await postCollection
    .doc(postId)
    .collection('highlights')
    .orderBy('order')
    .get();

  const highlights: IHighLight[] = [];
  results.forEach((doc: any) => {
    highlights.push({
      ...doc.data(),
      id: doc.id,
    });
  });

  return highlights;
};

export const updateNote = async (postId: string, note: object): Promise<void> => {
  return await postCollection
    .doc(postId)
    .collection('note')
    .doc(postId)
    .update(note);
};

export const getNote = async (postId: string): Promise<object> => {
  return await postCollection
    .doc(postId)
    .collection('note')
    .doc(postId)
    .get();
};

export const reportPost = async (post: IPost): Promise<void> => {
  return await postCollection
    .doc(post.id)
    .update({
      ...post,
      reported: true,
    });
};

export const approveReportedPost = async (post: IPost): Promise<void> => {
  const date = post.date || '';
  return await postCollection
    .doc(post.id)
    .update({
      ...post,
      date: firebase.firestore.Timestamp.fromDate(new Date(date)),
      reported: false,
    });
};

export const archivePost = async (post: IPost): Promise<void> => {
  return await postCollection
    .doc(post.id)
    .update({
      ...post,
      archived: true,
    });
};

export const postGroupIsClosed = (post: any): boolean => {
  return post && post.group && post.group.type === values.groupTypes.closed;
};

export const deleteAllPostsForUser = async (ownerId: String): Promise<void> => {
  return  await postCollection.where('owner', '==', ownerId)
      .get()
      .then((querySnapshot) => {
        querySnapshot.forEach((postDocument) => {
          const postData = postDocument.data();
          postCollection.doc(postDocument.id).collection('note').doc(postDocument.id).delete();
          postCollection.doc(postDocument.id).delete();
        });
      });
};
